import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * OnboardingGate Component
 * Checks if user has completed onboarding and has active subscription
 * Redirects to appropriate page if requirements not met
 */
const OnboardingGate = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Routes that should bypass the gate
  const bypassRoutes = [
    '/trial-activation',
    '/subscriptions',
    '/agency-onboarding',
    '/login',
    '/register',
    '/forgot-password'
  ];

  useEffect(() => {
    checkOnboardingAndSubscription();
  }, [location.pathname]);

  const checkOnboardingAndSubscription = async () => {
    // Check if current route should bypass gate
    if (bypassRoutes.some(route => location.pathname.startsWith(route))) {
      setIsAuthorized(true);
      setIsChecking(false);
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      
      if (!token) {
        navigate('/login');
        return;
      }

      // Check onboarding status
      const onboardingResponse = await axios.get(
        `${API_BASE_URL}/api/onboarding/status`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const onboardingCompleted = onboardingResponse.data.onboarding_completed;

      // Check subscription status
      const subscriptionResponse = await axios.get(
        `${API_BASE_URL}/api/subscriptions/current`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const hasSubscription = !!subscriptionResponse.data.subscription;
      const subscriptionStatus = subscriptionResponse.data.subscription?.status;

      // Logic:
      // 1. If no subscription -> redirect to trial activation
      // 2. If subscription exists but onboarding not complete -> redirect to onboarding
      // 3. If subscription expired -> allow access but show renewal prompt
      // 4. If all good -> allow access

      if (!hasSubscription) {
        toast.info('Please activate your trial or subscribe to continue');
        navigate('/trial-activation');
        return;
      }

      if (!onboardingCompleted) {
        toast.info('Please complete your onboarding to access the dashboard');
        navigate('/agency-onboarding');
        return;
      }

      // All checks passed
      setIsAuthorized(true);

    } catch (error) {
      console.error('Gate check error:', error);
      // On error, allow access but log the issue
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        setIsAuthorized(true);
      }
    } finally {
      setIsChecking(false);
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your access...</p>
        </div>
      </div>
    );
  }

  return isAuthorized ? children : null;
};

export default OnboardingGate;
